import axios from "axios";
import CourseDocument from "@/models/courseDocument";
import store from "@/store";
import { applyErrorHandlers } from '@/decorators/applyErrorHandler';

const API_COURSE_DOCUMENT_URL = process.env.VUE_APP_API_URL + "/course_documents";
const API_FILE_URL = process.env.VUE_APP_API_URL + "/files";

export default class CourseDocumentController {
    // Fetch course documents; pass a semesterId to scope, or null/undefined for all semesters.
    static async fetchCourseDocuments(semesterId) {
        const params = {};
        if (semesterId !== null && semesterId !== undefined) {
            params.semester_id = semesterId;
        }
        const response = await axios.get(`${API_COURSE_DOCUMENT_URL}/list`, { params });
        return response.data.map(doc => new CourseDocument(doc));
    }

    // Upload + index a course document for the current semester. `formData` must carry `file`.
    static async uploadCourseDocument(formData) {
        formData.append('semester_id', store.getters.semesterId);
        return await axios.post(`${API_COURSE_DOCUMENT_URL}/upload`, formData, {
            headers: { 'Content-Type': 'multipart/form-data' },
        });
    }

    static async deleteCourseDocument(documentId) {
        return await axios.delete(`${API_COURSE_DOCUMENT_URL}/${documentId}`);
    }

    // Publish/unpublish a document to students. When hidden, students can't list or
    // download it and the study chatbot won't ground answers on it.
    static async setCourseDocumentAvailability(documentId, isAvailable) {
        return await axios.patch(
            `${API_COURSE_DOCUMENT_URL}/${documentId}/availability`, { is_available: isAvailable });
    }

    // Assign a document to a set of homeworks (REPLACES the current set). The backend
    // reads the raw list[int] JSON body.
    static async assignCourseDocument(documentId, homeworkIds) {
        return await axios.post(`${API_COURSE_DOCUMENT_URL}/${documentId}/assign`, homeworkIds);
    }

    // Reuse the generic file-download endpoint (the course-doc file_path is a relative
    // path under the shared storage root, exactly like GlobalFileController does).
    static async downloadCourseDocument(filePath) {
        return await axios.get(`${API_FILE_URL}/download_file`, {
            params: { relative_file_path: filePath },
            responseType: 'blob',
        });
    }

    // Student/teacher read view: course materials for the caller's own semester.
    static async fetchAvailableCourseDocuments() {
        const response = await axios.get(`${API_COURSE_DOCUMENT_URL}/available`);
        return response.data.map(doc => new CourseDocument(doc));
    }

    // Download via the access-checked course-document endpoint (students may only
    // reach their own semester's materials; the backend enforces it by id).
    static async downloadAvailableCourseDocument(documentId) {
        return await axios.get(`${API_COURSE_DOCUMENT_URL}/${documentId}/download`, {
            responseType: 'blob',
        });
    }
}

applyErrorHandlers(CourseDocumentController);
